import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

/**
 * Hook pour optimiser les performances SEO
 */
export const useSEOOptimization = () => {
  const location = useLocation();
  const previousPath = useRef();
  const { t, i18n } = useTranslation();

  useEffect(() => {
    // Gestion du changement de page pour le SEO
    if (previousPath.current !== location.pathname) {
      // Scroll vers le haut lors du changement de page
      window.scrollTo(0, 0);

      // Mise à jour du titre de l'onglet si nécessaire
      updateDocumentTitle();

      // Analytics et tracking (Google Analytics, etc.)
      trackPageView();

      // Optimisation des images lazy loading
      optimizeLazyImages();

      // Preload des ressources critiques
      preloadCriticalResources();

      previousPath.current = location.pathname;
    }
  }, [location.pathname, t, i18n.language]); // Ajout des dépendances pour la traduction

  // Effet séparé pour mettre à jour le titre quand la langue change
  useEffect(() => {
    updateDocumentTitle();
  }, [i18n.language, t]);

  // Optimisation des images avec lazy loading
  const optimizeLazyImages = () => {
    if ('IntersectionObserver' in window) {
      const imageObserver = new IntersectionObserver((entries, observer) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const img = entry.target;
            if (img.dataset.src) {
              img.src = img.dataset.src;
              img.removeAttribute('data-src');
              img.classList.add('loaded');
              observer.unobserve(img);
            }
          }
        });
      });

      // Observer toutes les images avec data-src
      document.querySelectorAll('img[data-src]').forEach(img => {
        imageObserver.observe(img);
      });
    }
  };
  // Preload des ressources critiques
  const preloadCriticalResources = () => {
    const criticalPaths = {
      '/': [], // Suppression des appels API problématiques
      '/articles': [],
      '/pricing': [],
      '/dashboard': []
    };

    const currentResources = criticalPaths[location.pathname];
    if (currentResources) {
      currentResources.forEach(resource => {
        // Précharger les données via fetch avec cache
        fetch(resource, { 
          method: 'GET',
          headers: { 'X-Preload': 'true' }
        }).catch(() => {
          // Ignorer les erreurs de preload
        });
      });
    }
  };

  // Tracking des pages vues
  const trackPageView = () => {
    // Google Analytics 4
    if (typeof gtag !== 'undefined') {
      gtag('config', process.env.REACT_APP_GA_MEASUREMENT_ID, {
        page_title: document.title,
        page_location: window.location.href,
        page_path: location.pathname
      });
    }

    // Google Tag Manager
    if (typeof dataLayer !== 'undefined') {
      dataLayer.push({
        event: 'page_view',
        page_title: document.title,
        page_location: window.location.href,
        page_path: location.pathname
      });
    }
  };

  // Mise à jour du titre si pas géré par Helmet
  const updateDocumentTitle = () => {
    // Vérifier si Helmet gère déjà le titre
    const helmetMeta = document.querySelector('meta[data-react-helmet]');
    const helmetTitle = document.querySelector('title[data-react-helmet]');
    
    // Si Helmet ne gère pas le titre, on applique notre fallback
    if (!helmetMeta && !helmetTitle) {
      const siteName = t('seo.siteName', 'FlowDino');
      const pathTitles = {
        '/': `${t('seo.pages.home.title', 'Gestion et Partage d\'Articles Simplifiés')} | ${siteName}`,
        '/articles': `${t('seo.pages.articles.title', 'Articles')} | ${siteName}`,
        '/pricing': `${t('seo.pages.pricing.title', 'Tarifs')} | ${siteName}`,
        '/documentation': `${t('seo.pages.documentation.title', 'Documentation')} | ${siteName}`,
        '/dashboard': `${t('seo.pages.dashboard.title', 'Tableau de bord')} | ${siteName}`,
        '/profile': `${t('seo.pages.profile.title', 'Profil')} | ${siteName}`,
        '/login': `${t('seo.pages.login.title', 'Connexion')} | ${siteName}`,
        '/register': `${t('seo.pages.register.title', 'Inscription')} | ${siteName}`,
        '/support': `${t('seo.pages.support.title', 'Support et Aide')} | ${siteName}`,
        '/subscription': `${t('seo.pages.subscription.title', 'Mon Abonnement')} | ${siteName}`,
        '/privacy': `${t('seo.pages.legal.privacy.title', 'Politique de Confidentialité')} | ${siteName}`,
        '/legal/privacy': `${t('seo.pages.legal.privacy.title', 'Politique de Confidentialité')} | ${siteName}`,
        '/legal/cgu': `${t('seo.pages.legal.cgu.title', 'Conditions Générales d\'Utilisation')} | ${siteName}`,
        '/legal/notice': `${t('seo.pages.legal.notice.title', 'Mentions Légales')} | ${siteName}`
      };

      const title = pathTitles[location.pathname] || siteName;
      if (document.title !== title) {
        document.title = title;
      }
    }
  };

  // Optimisation des Core Web Vitals
  const optimizeWebVitals = () => {
    // Lazy loading des composants non critiques
    const nonCriticalComponents = document.querySelectorAll('[data-lazy-component]');
    
    if ('IntersectionObserver' in window) {
      const componentObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const element = entry.target;
            const componentName = element.dataset.lazyComponent;
            
            // Charger le composant dynamiquement
            import(`../components/${componentName}`)
              .then(module => {
                // Logique de rendu du composant
                element.removeAttribute('data-lazy-component');
              })
              .catch(err => console.warn(`Failed to load component: ${componentName}`, err));
            
            componentObserver.unobserve(element);
          }
        });
      }, {
        rootMargin: '50px'
      });

      nonCriticalComponents.forEach(component => {
        componentObserver.observe(component);
      });
    }
  };

  // Optimisation des performances de navigation
  const optimizeNavigation = () => {
    // Prefetch des liens visibles
    const links = document.querySelectorAll('a[href^="/"]');
    
    if ('IntersectionObserver' in window) {
      const linkObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const link = entry.target;
            const href = link.getAttribute('href');
            
            // Créer un link prefetch
            if (!document.querySelector(`link[rel="prefetch"][href="${href}"]`)) {
              const prefetchLink = document.createElement('link');
              prefetchLink.rel = 'prefetch';
              prefetchLink.href = href;
              document.head.appendChild(prefetchLink);
            }
          }
        });
      }, {
        rootMargin: '100px'
      });

      links.forEach(link => {
        linkObserver.observe(link);
      });
    }
  };

  useEffect(() => {
    optimizeWebVitals();
    optimizeNavigation();
  }, []);

  return {
    optimizeLazyImages,
    preloadCriticalResources,
    trackPageView
  };
};

export default useSEOOptimization;
